import React, { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { useTheme } from '../../contexts/ThemeContext';
import { FiSearch, FiChevronUp, FiChevronDown, FiChevronsLeft, FiChevronsRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

interface DataTableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
  isLoading?: boolean;
  emptyIcon?: React.ReactNode;
  emptyMessage?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  pageSize?: number;
  onRowClick?: (row: T) => void;
  expandableRow?: (row: T) => React.ReactNode;
  rowActions?: (row: T) => React.ReactNode;
}

function DataTable<T>({
  columns,
  data,
  isLoading = false,
  emptyIcon,
  emptyMessage = 'No data available',
  searchable = false,
  searchPlaceholder = 'Search...',
  pageSize = 25,
  onRowClick,
  expandableRow,
  rowActions,
}: DataTableProps<T>) {
  const { darkMode } = useTheme();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const headerBg = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const rowBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';

  if (isLoading) {
    return (
      <div className={`rounded-lg border ${borderColor} overflow-hidden`}>
        <div className={`${headerBg} px-4 py-3`}>
          <div className="h-4 w-48 bg-gray-700 rounded animate-pulse" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={`${rowBg} px-4 py-4 border-t ${borderColor} flex gap-4`}>
            {Array.from({ length: Math.min(columns.length, 5) }).map((_, j) => (
              <div key={j} className="h-3 flex-1 bg-gray-700 rounded animate-pulse" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`rounded-lg border ${borderColor} p-12 text-center`}>
        {emptyIcon && <div className="mb-3 opacity-40">{emptyIcon}</div>}
        <p className={textMuted}>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div>
      {searchable && (
        <div className="mb-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${borderColor} ${rowBg}`}>
            <FiSearch className={textMuted} />
            <input
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
              placeholder={searchPlaceholder}
              className={`flex-1 bg-transparent outline-none text-sm ${darkMode ? 'text-gray-200 placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'}`}
            />
          </div>
        </div>
      )}

      <div className={`rounded-xl border ${borderColor} overflow-x-auto shadow-sm ${darkMode ? 'shadow-black/20' : ''}`}>
        <table className="w-full text-sm">
          <thead className={headerBg}>
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => (
                  <th
                    key={header.id}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    className={`px-4 py-3 text-left font-medium ${textMuted} ${
                      header.column.getCanSort() ? 'cursor-pointer select-none hover:text-gray-200' : ''
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === 'asc' && <FiChevronUp className="w-3 h-3" />}
                      {header.column.getIsSorted() === 'desc' && <FiChevronDown className="w-3 h-3" />}
                    </div>
                  </th>
                ))}
                {rowActions && <th className={`px-4 py-3 w-16 ${textMuted}`} />}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => {
              const isExpanded = expandedRowId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={`${rowBg} border-t ${borderColor} ${
                      darkMode ? 'hover:bg-gray-700/40' : 'hover:bg-purple-50/50'
                    } transition-all duration-150 ${onRowClick || expandableRow ? 'cursor-pointer hover:shadow-sm' : ''} ${
                      isExpanded && darkMode ? 'bg-gray-700/20' : isExpanded ? 'bg-purple-50/30' : ''
                    }`}
                    onClick={() => {
                      if (expandableRow) setExpandedRowId(isExpanded ? null : row.id);
                      if (onRowClick) onRowClick(row.original);
                    }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className={`px-4 py-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    {rowActions && (
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        {rowActions(row.original)}
                      </td>
                    )}
                  </tr>
                  {isExpanded && expandableRow && (
                    <tr className={`border-t ${borderColor} ${darkMode ? 'bg-gray-800/60' : 'bg-purple-50/30'}`}>
                      <td colSpan={row.getVisibleCells().length + (rowActions ? 1 : 0)} className="px-0 py-0">
                        <div className={`flex border-l-4 ${darkMode ? 'border-purple-600/50' : 'border-purple-400'}`}>
                          <div className="px-6 py-4 flex-1">
                            {expandableRow(row.original)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {table.getPageCount() > 1 && (
        <div className={`flex items-center justify-between mt-3 text-sm ${textMuted}`}>
          <span>
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            {' \u00b7 '}{data.length} total
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}
              className={`p-1 rounded disabled:opacity-30 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
              <FiChevronsLeft className="w-4 h-4" />
            </button>
            <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}
              className={`p-1 rounded disabled:opacity-30 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}
              className={`p-1 rounded disabled:opacity-30 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
              <FiChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}
              className={`p-1 rounded disabled:opacity-30 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
              <FiChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
